import { zodResolver } from "@hookform/resolvers/zod";
import React, { useContext, useEffect, useMemo } from "react";
import ReactDatePicker from "react-datepicker";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { generateDefaultValues, schema, Schema } from "./schema";
import useRefreshLocationData, {
  RefreshType,
} from "../../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { ConfirmCloseContext } from "../../../../contexts/confirmClose";
import { LocationData } from "../../../../models/LocationData";
import DataSvc from "../../../../services/dataSvc";
import { HookForm } from "../../../HookForm/HookForm";

export interface IOpenInfoSectionProps {
  locationData: LocationData;
  accordionEventKey: string;
}

export const OpenInfoSection = (props: IOpenInfoSectionProps) => {
  const { locationData, accordionEventKey } = props;

  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityDetailsPage.modalEditProfile.openInfoForm.${key}`);

  const refresh = useRefreshLocationData(locationData.id.toString());

  const defaultValues = useMemo(() => {
    return generateDefaultValues(locationData);
  }, [locationData]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmit = async (data: Schema) => {
    let existingOpenInfo = {};
    if (locationData.open_info) {
      existingOpenInfo = JSON.parse(locationData.open_info);
    }

    // Opening date can be set to null in order to remove the opening date from the location profile.

    const date = data.openingDate ? new Date(data.openingDate) : undefined;
    const payload = {
      location: {
        openInfo: {
          ...existingOpenInfo,
          openingDate: date
            ? {
                year: date.getFullYear(),
                month: date.getMonth() + 1,
                day: date.getDate(),
              }
            : undefined,
        },
      },
    };
    try {
      await DataSvc.updateLocationProfile(locationData.id, payload);
      toast.success(t("success"));
      refresh(RefreshType.LOCATION);
      form.reset(data);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const { register, unregister } = useContext(ConfirmCloseContext);

  useEffect(() => {
    if (form.formState.isDirty) {
      register({
        key: accordionEventKey,
        message: "dirty",
        onCloseCallback: form.reset,
      });
    } else {
      unregister(accordionEventKey);
    }
  }, [form.formState.isDirty, register, unregister, accordionEventKey, form]);

  return (
    <HookForm onSubmit={onSubmit} form={form} submitButtonLabel={t("update")}>
      <p className="text-muted mb-3">{t("description")}</p>
      <div className="form-group">
        <label>{t("opening_date")}</label>
        <Controller
          control={form.control}
          name="openingDate"
          render={({ field }) => (
            <ReactDatePicker
              className="form-control"
              locale="ja"
              dateFormat="yyyy/MM/dd"
              selected={field.value}
              onChange={field.onChange}
              placeholderText={t("select_date")}
              maxDate={new Date()}
            />
          )}
        />
      </div>
    </HookForm>
  );
};
